import React, {ReactElement, Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useAvailableComponentKr, {SeoulMileageSupportedType} from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface MileageConditionalRendererProps {
  errorFallback?: ReactElement;
  loadingFallback?: ReactElement;
  vin: string;

  ifAgree(checkAndGotoMileagePage: () => void): ReactElement;

  ifSupportedNotAgree(openMileageSingUpBottomSheet: () => void): ReactElement;

  ifNotSupported(): ReactElement;
}

{
  /**
   * [2025-04-25:Jeyoung Lee] FIXME:테스트 해봐야 하는거
   * [x] 서울시 지원x -> 미노출
   * [x] 서울시 지원o, 동의x -> 모달 뜨고나서 인트로 페이지로 이동해야함
   * [x] 서울시 지원o, 동의o, PENDING_APPROVAL -> alert 띄우기
   * [x] 서울시 지원o, 동의o, PERFORMANCE_REGISTRATION_REQUIRED -> 실적등록 페이지로 이동
   * [x] 서울시 지원o, 동의o, MILEAGE_ACCESS_ALLOWED -> 마일리지 페이지로 이동
   *
   */
}

function MileageConditionalRenderer({
  ifSupportedNotAgree,
  ifNotSupported,
  ifAgree,
  vin,
  loadingFallback = <></>,
  errorFallback = <></>,
}: MileageConditionalRendererProps): ReactElement {
  const {seoulMileageSupportedType} = useAvailableComponentKr({vin});
  const navigate = useNavigate();

  async function openMileageSingUpBottomSheet() {
    const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
      ROUTE_PATHS.BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY.getFullPath()
    );
    if (isConfirm) {
      navigate(ROUTE_PATHS.MILEAGE_INTRO.getFullPath(vin));
    }
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.NOT_SUPPORTED) {
    return ifNotSupported();
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.SUPPORTED_NOT_AGREE) {
    return ifSupportedNotAgree(openMileageSingUpBottomSheet);
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.SUPPORTED_AGREE) {
    return (
      <ErrorBoundary fallback={errorFallback}>
        <Suspense fallback={loadingFallback}>
          <MileageNavigator vin={vin}>{(checkAndGotoMileagePage) => ifAgree(checkAndGotoMileagePage)}</MileageNavigator>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return <></>;
}

export default MileageConditionalRenderer;

interface MileageNavigatorProps {
  vin: string;

  children(checkAndGotoMileagePage: () => void): ReactElement;
}

function MileageNavigator({vin, children}: MileageNavigatorProps) {
  const {openAlert} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
    queryFn: () => diApi.ecoMileageController.getInfoLink({vin}),
  });

  const {drvng_stt_cd} = data.data.rtn_data;

  /**
   * drvng_stt_cd 에 대한 설명
   * @link https://hmg.dooray.com/project/tasks/4208274598827184933
   */
  async function checkAndGotoMileagePage() {
    /**
     * [2026-02-06:Jeyoung Lee] FIXME:26년 서울시 정책 변경으로 에코마일리지 등록 가능 차량에서 하이브리드 차량 제외됨 (임시 drvng_stt_cd === 000)
     * @link https://hmg.atlassian.net/browse/DRIVINGINS-2884
     * 서울시 26년 정책 변경 대응
     * @link https://hmg.atlassian.net/browse/DRIVINGINS-2955
     */
    if (drvng_stt_cd === '000') {
      navigate(`${ROUTE_PATHS.MILEAGE_BALANCE.getFullPath(vin)}?hideTab=eco|safe`);
      return;
    }

    if (drvng_stt_cd === '032') {
      // 중간주행거리 등록 이전, 기준주행거리 업데이트중, 중간주행거리 등록 컴포넌트
      navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin));
      return;
    }

    if (drvng_stt_cd === '031') {
      // 중간주행거리 등록페이지
      navigate(ROUTE_PATHS.MILEAGE_MID_REGISTER.getFullPath(vin));
      return;
    }

    if (drvng_stt_cd === '022') {
      // 구제도 도전중
      navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin));
      return;
    }

    if (drvng_stt_cd === '021' || drvng_stt_cd === '051' || drvng_stt_cd === '071') {
      navigate(ROUTE_PATHS.MILEAGE_REGISTER.getFullPath(vin)); // 도전종료! 실적 등록이 필요해요
      // 실적 등록 페이지
      return;
    }

    if (drvng_stt_cd === '061') {
      // 최초 주행거리 등록
      navigate(ROUTE_PATHS.MILEAGE_FIRST_REGISTER.getFullPath(vin));
      return;
    }

    if (drvng_stt_cd === '063') {
      // 신제도 도전중
      navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin));
      return;
    }

    if (drvng_stt_cd === '023' || drvng_stt_cd === '073') {
      navigate(ROUTE_PATHS.MILEAGE_PARTICIPATION_GUIDE.getFullPath(vin)); // 도전이 종료되었습니다.
      return;
    }

    await openAlert({
      title: t('T30205'),
      message: t('T30206_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
    });
  }

  return children(checkAndGotoMileagePage);
}
